import ScalarLoading_default from "../ScalarLoading/ScalarLoading.vue.js";
import { filterByOptionLabel } from "./filter-by-option-label.js";
import ScalarListboxCheckbox_default from "../ScalarListbox/ScalarListboxCheckbox.vue.js";
import ScalarComboboxOption_default from "./ScalarComboboxOption.vue.js";
import ScalarComboboxOptionGroup_default from "./ScalarComboboxOptionGroup.vue.js";
import { isGroups } from "./types.js";
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createTextVNode, createVNode, defineComponent, mergeModels, normalizeClass, onMounted, openBlock, ref, renderList, renderSlot, toDisplayString, unref, useId, useModel, useSlots, vModelText, vShow, watch, withCtx, withDirectives, withKeys, withModifiers } from "vue";
import { ScalarIconMagnifyingGlass, ScalarIconPlus } from "@scalar/icons";
//#region src/components/ScalarCombobox/ScalarComboboxOptions.vue?vue&type=script&setup=true&lang.ts
var _hoisted_1 = { class: "relative m-1 mb-0.75 flex items-center" };
var _hoisted_2 = [
	"aria-activedescendant",
	"aria-expanded",
	"aria-label",
	"placeholder"
];
var _hoisted_3 = {
	key: 0,
	class: "absolute top-1/2 right-1.75 flex -translate-y-1/2 items-center"
};
var _hoisted_4 = ["aria-busy", "aria-multiselectable"];
var _hoisted_5 = {
	key: 1,
	class: "text-c-3 px-2.5 py-2",
	role: "status"
};
var _hoisted_6 = { class: "text-c-1 inline-block min-w-0 flex-1 truncate" };
var ScalarComboboxOptions_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	inheritAttrs: false,
	__name: "ScalarComboboxOptions",
	props: /*@__PURE__*/ mergeModels({
		options: {},
		placeholder: {},
		inputLabel: {},
		noResults: {},
		close: { type: Function },
		filterFn: {
			type: Function,
			default: filterByOptionLabel
		},
		loader: {},
		multiselect: { type: Boolean }
	}, {
		"modelValue": { default: () => [] },
		"modelModifiers": {},
		"query": { default: "" },
		"queryModifiers": {}
	}),
	emits: /*@__PURE__*/ mergeModels(["add"], ["update:modelValue", "update:query"]),
	setup(__props, { emit: __emit }) {
		const emit = __emit;
		const model = useModel(__props, "modelValue");
		const slots = useSlots();
		/** A unique ID for the combobox */
		const id = `scalar-combobox-items-${useId()}`;
		/** A static option entry for the "Add a new option" option */
		const addOption = {
			id: `${useId()}-add`,
			label: "Add a new option"
		};
		/** Generate a unique ID for an option */
		function getOptionId(option) {
			return `${id}-${option.id}`;
		}
		/** A flat list of all options */
		const options = computed(() => isGroups(__props.options) ? __props.options.flatMap((group) => group.options) : __props.options);
		/** An list of all groups */
		const groups = computed(() => isGroups(__props.options) ? __props.options : [{
			label: "",
			options: __props.options
		}]);
		/** The search query, exposed so callers can search the options themselves */
		const query = useModel(__props, "query");
		/** The filtered list of options */
		const filtered = computed(() => __props.filterFn(query.value, options.value, groups.value));
		/** The list of filtered options with the "Add a new option" option */
		const withAdd = computed(() => slots.add ? [...filtered.value, addOption] : filtered.value);
		/**
		* The selected option when it is in the list, otherwise the top option.
		*
		* With async search the selected option is often not in the loaded results (e.g. a branch
		* past the first page), and pointing at a row that does not exist would let Enter select nothing.
		*/
		const getInitialActive = () => {
			const selected = model.value[0];
			return selected && withAdd.value.some((option) => option.id === selected.id) ? selected : withAdd.value[0];
		};
		const activeRef = ref(getInitialActive());
		onMounted(() => {
			query.value = "";
			activeRef.value = getInitialActive();
			if (model.value[0]) setTimeout(() => {
				const value = model.value[0];
				if (!value) return;
				document?.getElementById(getOptionId(value))?.scrollIntoView({ block: "nearest" });
			}, 10);
		});
		/**
		* Whether the query changed since the options last updated. Async results arrive after the
		* query changes, so the top hit of the new results should become active even when the
		* previously active option is still somewhere in the list.
		*/
		let queryChanged = false;
		watch(() => query.value, () => {
			activeRef.value = withAdd.value[0];
			queryChanged = true;
		});
		/**
		* Async options arrive after the query has changed, so the active option can point at a row
		* that no longer exists or is no longer the top hit. Move it back to the top when that happens.
		*/
		watch(withAdd, (list) => {
			if (queryChanged || !list.some((option) => option.id === activeRef.value?.id)) activeRef.value = list[0];
			queryChanged = false;
		});
		/** Whether the options are still loading */
		const isLoading = computed(() => Boolean(__props.loader?.isLoading));
		/** Show placeholder rows while loading with nothing to show yet */
		const showSkeleton = computed(() => isLoading.value && !filtered.value.length);
		/** Show the loader in the search input, unless the skeleton already says it is loading */
		const showLoader = computed(() => Boolean(__props.loader?.isActive) && !showSkeleton.value);
		/** Whether there is anything to show at the end of the search input */
		const showSearchEnd = computed(() => Boolean(slots["search-end"] || showLoader.value));
		/** Whether the listbox has anything to show */
		const showList = computed(() => Boolean(filtered.value.length || slots.add || showSkeleton.value || __props.noResults && !isLoading.value));
		function toggleSelected(option) {
			if (!option) return;
			if (option.id === addOption.id) {
				addNew();
				return;
			}
			if (__props.multiselect && model.value.some((o) => o.id === option.id)) {
				model.value = model.value.filter((o) => o.id !== option.id);
				return;
			}
			const match = options.value.find((o) => o.id === option.id);
			if (!match) return;
			model.value = __props.multiselect ? [...model.value, match] : [match];
		}
		function moveActive(dir) {
			const list = withAdd.value;
			const nextIdx = list.findIndex((option) => option.id === activeRef.value?.id) + dir;
			if (nextIdx < 0 || nextIdx > list.length - 1) return;
			activeRef.value = list[nextIdx];
			if (!activeRef.value) return;
			document?.getElementById(getOptionId(activeRef.value))?.scrollIntoView({
				behavior: "smooth",
				block: "nearest"
			});
		}
		function addNew() {
			emit("add");
			query.value = "";
		}
		/**
		* Space toggles the active option in multiselect mode while the search box is empty.
		*
		* The options look like checkboxes, so keyboard users press Space to toggle them. Without this
		* handler Space types a leading space into the query, which filters out every option whose label
		* has no space and hides the whole list without any feedback (found by an accessibility audit).
		* Once a query has been typed, Space keeps inserting a character so multi-word labels stay
		* searchable. Key auto-repeat is ignored so holding Space after opening the popover with it does
		* not toggle the option repeatedly, and IME composition is left alone.
		*/
		const handleSpace = (event) => {
			if (!__props.multiselect || query.value !== "" || event.isComposing) return;
			event.preventDefault();
			if (!event.repeat) toggleSelected(activeRef.value);
		};
		const input = ref(null);
		onMounted(() => setTimeout(() => input.value?.focus(), 0));
		return (_ctx, _cache) => {
			return openBlock(), createElementBlock(Fragment, null, [createElementVNode("div", _hoisted_1, [
				createVNode(unref(ScalarIconMagnifyingGlass), { class: "text-c-3 pointer-events-none absolute top-1/2 left-1.75 size-4 -translate-y-1/2" }),
				withDirectives(createElementVNode("input", {
					ref_key: "input",
					ref: input,
					"onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => query.value = $event),
					"aria-activedescendant": activeRef.value ? getOptionId(activeRef.value) : void 0,
					"aria-autocomplete": "list",
					"aria-controls": id,
					"aria-expanded": showList.value,
					"aria-label": __props.inputLabel,
					class: normalizeClass(["text-c-1 min-w-0 flex-1 rounded-md border-0 py-1.5 pl-7.25 leading-none", showSearchEnd.value ? "pr-7.25" : "pr-1.75"]),
					"data-1p-ignore": "",
					placeholder: __props.placeholder,
					role: "combobox",
					tabindex: "0",
					type: "text",
					onKeydown: [
						_cache[1] || (_cache[1] = withKeys(withModifiers(($event) => moveActive(1), ["prevent"]), ["down"])),
						_cache[2] || (_cache[2] = withKeys(withModifiers(($event) => activeRef.value && toggleSelected(activeRef.value), ["prevent"]), ["enter"])),
						_cache[3] || (_cache[3] = withKeys(withModifiers(($event) => __props.close?.(), ["prevent"]), ["esc"])),
						withKeys(handleSpace, ["space"]),
						_cache[4] || (_cache[4] = withKeys(withModifiers(($event) => moveActive(-1), ["prevent"]), ["up"]))
					]
				}, null, 42, _hoisted_2), [[vModelText, query.value]]),
				showSearchEnd.value ? (openBlock(), createElementBlock("div", _hoisted_3, [renderSlot(_ctx.$slots, "search-end", { loader: __props.loader }, () => [showLoader.value ? (openBlock(), createBlock(unref(ScalarLoading_default), {
					key: 0,
					loader: __props.loader,
					size: "md"
				}, null, 8, ["loader"])) : createCommentVNode("", true)])])) : createCommentVNode("", true)
			]), withDirectives(createElementVNode("ul", {
				id,
				"aria-busy": isLoading.value,
				"aria-multiselectable": __props.multiselect,
				class: "custom-scroll min-h-0 flex-1 overscroll-contain border-t p-0.75",
				role: "listbox",
				tabindex: "-1"
			}, [
				showSkeleton.value ? (openBlock(), createElementBlock(Fragment, { key: 0 }, renderList(3, (idx) => {
					return createElementVNode("li", {
						key: idx,
						"aria-hidden": "true",
						class: "flex px-2 py-1.5",
						role: "presentation"
					}, [createElementVNode("div", { class: normalizeClass(["bg-b-3 h-lh animate-pulse rounded-md", idx === 3 ? "w-2/3" : "w-full"]) }, null, 2)]);
				}), 64)) : !filtered.value.length && !slots.add && __props.noResults && !isLoading.value ? (openBlock(), createElementBlock("li", _hoisted_5, toDisplayString(__props.noResults), 1)) : createCommentVNode("", true),
				(openBlock(true), createElementBlock(Fragment, null, renderList(groups.value, (group, i) => {
					return openBlock(), createBlock(ScalarComboboxOptionGroup_default, {
						id: `${id}-group-${i}`,
						key: i,
						hidden: !group.options.some((o) => filtered.value.some((f) => f.id === o.id)) || !group.label
					}, {
						label: withCtx(() => [_ctx.$slots.group ? renderSlot(_ctx.$slots, "group", { group }, void 0, void 0, 0) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [createTextVNode(toDisplayString(group.label), 1)], 64))]),
						default: withCtx(() => [(openBlock(true), createElementBlock(Fragment, null, renderList(filtered.value, (option) => {
							return openBlock(), createElementBlock(Fragment, { key: option.id }, [group.options.some((o) => o.id === option.id) ? (openBlock(), createBlock(ScalarComboboxOption_default, {
								key: 0,
								id: getOptionId(option),
								active: activeRef.value?.id === option.id,
								selected: model.value.some((o) => o.id === option.id),
								onClick: ($event) => toggleSelected(option),
								onMousedown: _cache[5] || (_cache[5] = withModifiers(() => {}, ["prevent"])),
								onMouseenter: ($event) => activeRef.value = option
							}, {
								default: withCtx(({ active, selected }) => [_ctx.$slots.option ? renderSlot(_ctx.$slots, "option", {
									active,
									option,
									selected
								}, void 0, void 0, 0) : (openBlock(), createElementBlock(Fragment, { key: 1 }, [createVNode(unref(ScalarListboxCheckbox_default), {
									multiselect: __props.multiselect,
									selected: model.value.some((o) => o.id === option.id)
								}, null, 8, ["multiselect", "selected"]), createElementVNode("span", _hoisted_6, toDisplayString(option.label), 1)], 64))]),
								_: 2
							}, 1032, [
								"id",
								"active",
								"selected",
								"onClick",
								"onMouseenter"
							])) : createCommentVNode("", true)], 64);
						}), 128))]),
						_: 2
					}, 1032, ["id", "hidden"]);
				}), 128)),
				slots.add ? (openBlock(), createBlock(ScalarComboboxOption_default, {
					key: 2,
					id: getOptionId(addOption),
					active: activeRef.value?.id === addOption.id,
					onClick: addNew,
					onMousedown: _cache[6] || (_cache[6] = withModifiers(() => {}, ["prevent"])),
					onMouseenter: _cache[7] || (_cache[7] = ($event) => activeRef.value = addOption)
				}, {
					default: withCtx(({ active }) => [createVNode(unref(ScalarIconPlus), { class: "size-4 p-px" }), renderSlot(_ctx.$slots, "add", { active })]),
					_: 3
				}, 8, ["id", "active"])) : createCommentVNode("", true)
			], 8, _hoisted_4), [[vShow, showList.value]])], 64);
		};
	}
});
//#endregion
export { ScalarComboboxOptions_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=ScalarComboboxOptions.vue_vue_type_script_setup_true_lang.js.map